import { Copy, CopyPlus, Edit, Lock, RotateCcw, Trash } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { LocalAddress } from '@/context/create-account-flow';
import { useToast } from '@/hooks/use-toast';

interface AddressCardProps {
  title?: string;
  address: LocalAddress;
  onRefresh?: () => void;
  onEdit?: (address: LocalAddress) => void;
  onDelete?: (addressId: string) => void;
  onDuplicate?: (address: LocalAddress) => void;
}

export function AddressCard({
  title = 'Endereço',
  address,
  onRefresh,
  onEdit,
  onDelete,
  onDuplicate,
}: AddressCardProps) {
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      const { street, number, district, city, state, zip_code, complement, reference_point } =
        address;

      let text = `${street}, ${number}, ${district}\n${city} - ${state}, ${zip_code}`;

      if (complement) {
        text += `\nComplemento: ${complement}`;
      }

      if (reference_point) {
        text += `\nPonto de Referência: ${reference_point}`;
      }

      await navigator.clipboard.writeText(text);

      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Endereço copiado para a área de transferência.',
      });
    } catch (_error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o endereço.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(address);
    }
  };

  const handleDelete = () => {
    if (onDelete && address.id) {
      onDelete(address.id);
    }
  };

  const formatAddress = () => {
    const parts = [];

    if (address.street && address.number && address.district) {
      parts.push(`${address.street}, ${address.number}, ${address.district}`);
    }

    if (address.city && address.state && address.zip_code) {
      parts.push(`${address.city} - ${address.state}, ${address.zip_code}`);
    }

    return parts;
  };

  const addressParts = formatAddress();

  const isFiscalApi = address.type?.includes('Fiscal') ?? false;

  return (
    <TooltipProvider>
      <Card className="w-full max-w-sm transition-all duration-200 hover:shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap gap-1">
                {address.type &&
                  address.type.length > 0 &&
                  address.type.map((type) => (
                    <Badge key={type} variant="outline" className="text-xs font-medium">
                      {type}
                    </Badge>
                  ))}
                {isFiscalApi && (
                  <Badge variant="secondary" className="text-xs font-medium gap-1">
                    <Lock className="h-3 w-3" />
                    Dados da Receita
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex gap-1">
              {onRefresh && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={onRefresh}
                      aria-label="Atualizar endereço"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Atualizar</p>
                  </TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={handleCopy}
                    aria-label="Copiar endereço"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Copiar</p>
                </TooltipContent>
              </Tooltip>

              {isFiscalApi && onDuplicate && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => onDuplicate(address)}
                      aria-label="Duplicar endereço"
                    >
                      <CopyPlus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Duplicar</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {!isFiscalApi && onEdit && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={handleEdit}
                      aria-label="Editar endereço"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Editar endereço</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {!isFiscalApi && onDelete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={handleDelete}
                      aria-label="Excluir Endereço"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Excluir Endereço</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          <div className="space-y-2">
            {addressParts.map((part, index) => (
              <div
                key={part}
                className={index === 0 ? 'font-medium text-sm' : 'text-sm text-muted-foreground'}
              >
                {part}
              </div>
            ))}

            {(address.complement || address.reference_point) && (
              <>
                <Separator className="my-3" />
                <div className="space-y-1">
                  {address.complement && (
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Complemento:</span>{' '}
                      {address.complement}
                    </div>
                  )}
                  {address.reference_point && (
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Ponto de Referência:</span>{' '}
                      {address.reference_point}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
